import { useState } from "react"
import { ChevronDown, ChevronRight, Settings } from "lucide-react"
import { Card } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/cn"

type Props = {
  skill: Record<string, unknown>
}

// Body-only version of the setup view. No Card wrapper, no header —
// suitable for embedding inside a tabbed container (DesignerPane uses
// this inside a Setup/Pending tab strip). Use SkillSetupPanel below
// for the standalone Card-wrapped variant.
export function SkillSetupBody({ skill }: Props) {
  const description       = (skill.description as string) ?? ""
  const promptMd          = (skill.promptMd as string) ?? ""
  const notes             = (skill.notes as string) ?? ""
  const toolKeys          = (Array.isArray(skill.toolKeys) ? skill.toolKeys as string[] : [])
  const toolConfigs       = (skill.toolConfigs && typeof skill.toolConfigs === "object" ? skill.toolConfigs as Record<string, unknown> : {})
  const dataCollection    = (Array.isArray(skill.dataCollection) ? skill.dataCollection as unknown[] : [])
  const requiresUserState = (skill.requiresUserState as string | null) ?? null
  const bannedReadable    = !!skill.bannedUserReadable

  return (
    <div className="flex-1 space-y-4 overflow-y-auto px-4 py-3 text-sm">
      {description && (
        <Section label="Description">
          <p className="text-app-text">{description}</p>
        </Section>
      )}

      <Collapsible label="Prompt" defaultOpen={true} count={promptMd ? `${promptMd.length} chars` : "empty"}>
        {promptMd
          ? <pre className="rounded bg-app-bg p-3 text-[12px] whitespace-pre-wrap text-app-text">{promptMd}</pre>
          : <p className="text-app-subtle">(empty — Forge can fill this in)</p>}
      </Collapsible>

      <Section label="Tools" count={toolKeys.length}>
        {toolKeys.length === 0 ? (
          <span className="text-app-subtle">no tools wired</span>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {toolKeys.map((k) => (
              <code key={k} className="rounded-chip border border-border bg-white/5 px-2 py-0.5 text-[11px] text-app-text">
                {k}
              </code>
            ))}
          </div>
        )}
      </Section>

      {Object.keys(toolConfigs).length > 0 && (
        <Collapsible label="Tool configs" count={Object.keys(toolConfigs).length}>
          <pre className="rounded bg-app-bg p-3 text-[11px] whitespace-pre-wrap text-app-text">
            {JSON.stringify(toolConfigs, null, 2)}
          </pre>
        </Collapsible>
      )}

      {dataCollection.length > 0 && (
        <Collapsible label="Data collection" count={dataCollection.length}>
          <pre className="rounded bg-app-bg p-3 text-[11px] whitespace-pre-wrap text-app-text">
            {JSON.stringify(dataCollection, null, 2)}
          </pre>
        </Collapsible>
      )}

      {(requiresUserState || bannedReadable) && (
        <Section label="Visibility">
          <ul className="space-y-1 text-app-subtle text-xs">
            {requiresUserState && (
              <li>requires_user_state: <code className="text-app-text">{requiresUserState}</code></li>
            )}
            {bannedReadable && <li>visible to banned users</li>}
          </ul>
        </Section>
      )}

      {notes && (
        <Collapsible label="Notes">
          <p className="whitespace-pre-wrap text-app-subtle text-xs">{notes}</p>
        </Collapsible>
      )}
    </div>
  )
}

// Standalone Card-wrapped variant. Used wherever we want the setup view
// to stand on its own (not as a tab body).
export function SkillSetupPanel({ skill }: Props) {
  const version = skill.version
  const state   = (skill.state as string) ?? ""
  const stateTone: "gold" | "red" | "neutral" = state === "live" ? "gold" : state === "draft" ? "red" : "neutral"

  return (
    <Card className="flex h-full min-h-0 flex-col p-0">
      <header className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <Settings size={14} strokeWidth={1.7} className="text-app-muted" />
          <div>
            <h3 className="text-sm font-semibold">Current setup</h3>
            <p className="text-[10px] text-app-subtle">Persisted skill state</p>
          </div>
        </div>
        <Badge tone={stateTone}>v{String(version)} {state}</Badge>
      </header>
      <SkillSetupBody skill={skill} />
    </Card>
  )
}

function Section({ label, count, children }: { label: string; count?: number | string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1 flex items-baseline gap-2 text-[10px] uppercase tracking-wide text-app-subtle">
        <span>{label}</span>
        {count !== undefined && <span className="text-app-subtle">{count}</span>}
      </div>
      {children}
    </div>
  )
}

function Collapsible({
  label, count, defaultOpen, children,
}: { label: string; count?: number | string; defaultOpen?: boolean; children: React.ReactNode }) {
  const [open, setOpen] = useState(defaultOpen ?? false)
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-1.5 text-left"
      >
        {open ? <ChevronDown size={12} strokeWidth={2} /> : <ChevronRight size={12} strokeWidth={2} />}
        <span className="text-[10px] uppercase tracking-wide text-app-subtle">{label}</span>
        {count !== undefined && (
          <span className={cn("text-[10px] text-app-subtle", !open && "ml-auto")}>{count}</span>
        )}
      </button>
      {open && <div className="mt-1.5">{children}</div>}
    </div>
  )
}
